import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';

import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';
import {
    defaultTQC1020_R01ResultDto,
    TQC1020_R01HeaderDto,
    TQC1020_R01DetailDto,
} from './Dto_TQC1020_R01';

export interface TQC1020_R01Filter {
    compCd: string;
    transportDt: DateRange | undefined;
    customCd: string;
    customNm: string;
    itemCd: string;
    itemNm: string;
    inoutClass: string;
    includeCompleted: boolean;
}

export const defaultTQC1020_R01Filter: TQC1020_R01Filter = {
    compCd: '',
    transportDt: getCurrentMonthDateRange(),
    customCd: '',
    customNm: '',
    itemCd: '',
    itemNm: '',
    inoutClass: '',
    includeCompleted: false,
};

interface Store_TQC1020_R01 {
    // ── 필터 ────────────────────────────────────────────────────────────
    TQC1020_R01Filter: TQC1020_R01Filter;
    setTQC1020_R01Filter: (filter: Partial<TQC1020_R01Filter>) => void;

    // ── 상단 그리드 ──────────────────────────────────────────────────────
    TQC1020_R01List: TQC1020_R01HeaderDto[];
    setTQC1020_R01List: (list: TQC1020_R01HeaderDto[]) => void;

    selectedTQC1020_R01: TQC1020_R01HeaderDto | undefined;
    setSelectedTQC1020_R01: (item: TQC1020_R01HeaderDto | undefined) => void;

    // ── 하단 그리드 (검사결과) ───────────────────────────────────────────
    TQC1020_R01DetailList: TQC1020_R01DetailDto[];
    setTQC1020_R01DetailList: (data: TQC1020_R01DetailDto[]) => void;
    insertTQC1020_R01DetailList: (data: TQC1020_R01DetailDto[], index?: number) => void;
    removeTQC1020_R01DetailList: (valueIndex: number) => void;
    updateTQC1020_R01DetailList: (valueIndex: number, data: TQC1020_R01DetailDto) => void;

    // 선택 상태
    selectedTQC1020_R01DetailList: string[];
    setSelectedTQC1020_R01DetailList: (idList: string[]) => void;
    addSelectedTQC1020_R01DetailList: (idList: string[]) => void;
    removeSelectedTQC1020_R01DetailList: (idList?: string[]) => void;
    initSelectedTQC1020_R01DetailList: () => void;

    // 삭제 표시
    deletedTQC1020_R01DetailList: string[];
    setDeletedTQC1020_R01DetailList: (idList: string[]) => void;
    addDeletedTQC1020_R01DetailList: (idList: string[]) => void;
    removeDeletedTQC1020_R01DetailList: (idList?: string[]) => void;

    // 원본 (변경 감지용)
    originalTQC1020_R01DetailList: TQC1020_R01DetailDto[];
    setOriginalTQC1020_R01DetailList: (data: TQC1020_R01DetailDto[]) => void;

    // ── 컬럼 상태 ────────────────────────────────────────────────────────
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
    detailColumnState: ColumnState[] | null;
    setDetailColumnState: (state: ColumnState[] | null) => void;

    detailFetchTrigger: boolean;
    setDetailFetchTrigger: (value: boolean) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
    detailScrollTop: number | null;
    setDetailScrollTop: (top: number | null) => void;

    initData: () => void;
}

export const useTQC1020_R01Store = create<Store_TQC1020_R01>((set) => ({
    // ── 필터 ────────────────────────────────────────────────────────────
    TQC1020_R01Filter: defaultTQC1020_R01Filter,
    setTQC1020_R01Filter: (filter) =>
        set((state) => ({
            TQC1020_R01Filter: { ...state.TQC1020_R01Filter, ...filter },
        })),

    // ── 상단 그리드 ──────────────────────────────────────────────────────
    TQC1020_R01List: [],
    setTQC1020_R01List: (dataList) =>
        set((state) => ({
            ...state,
            TQC1020_R01List: dataList,
        })),

    selectedTQC1020_R01: undefined,
    setSelectedTQC1020_R01: (item) =>
        set({
            selectedTQC1020_R01: item,
            TQC1020_R01DetailList: [],
            originalTQC1020_R01DetailList: [],
            selectedTQC1020_R01DetailList: [],
            deletedTQC1020_R01DetailList: [],
            detailFetchTrigger: !!item,
            detailScrollTop: null,
        }),

    // ── 하단 그리드 ──────────────────────────────────────────────────────
    // PUR1030의 setPUR1030_R01DetailList 패턴 그대로
    TQC1020_R01DetailList: [],
    setTQC1020_R01DetailList: (data) =>
        set((state) => {
            let newList: TQC1020_R01DetailDto[] = [];
            let valueAndOrderIndex = 1;
            data.forEach((item) => {
                newList.push({
                    ...item,
                    valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
                    orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
                    orderIndexSeq: Math.max(item.orderIndexSeq, 0),
                });
                valueAndOrderIndex++;
            });
            return { ...state, TQC1020_R01DetailList: newList };
        }),

    // PUR1030의 insertPUR1030_R01DetailList 패턴 그대로
    insertTQC1020_R01DetailList: (data, index?) =>
        set((state) => {
            let newList = [...state.TQC1020_R01DetailList];

            const targetRowData = newList.find((item) => item.valueIndex === index);
            const targetOrderIndex = targetRowData?.orderIndex || 0;
            const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;

            let maxValueIndex = Math.max(0, ...newList.map((item) => item.valueIndex || 0)) + 1;

            // targetOrderIndex 행들의 orderIndexSeq 밀기
            const targetRows = newList.filter((item) => item.orderIndex === targetOrderIndex);
            targetRows.forEach((item) => {
                if (item.orderIndexSeq >= newOrderIndexSeq) {
                    item.orderIndexSeq = item.orderIndexSeq + 1;
                }
            });

            newList = newList.filter((item) => item.orderIndex !== targetOrderIndex);
            newList.push(...targetRows);

            const newData = data.map((item) => ({
                ...defaultTQC1020_R01ResultDto,
                ...item,
                valueIndex: maxValueIndex++,
                orderIndex: targetOrderIndex,
                orderIndexSeq: newOrderIndexSeq,
            }));

            if (index !== undefined) {
                newList.splice(index, 0, ...newData);
            } else {
                newList.push(...newData);
            }

            return { ...state, TQC1020_R01DetailList: newList };
        }),

    // PUR1030의 removePUR1030_R01DetailList 패턴 그대로
    removeTQC1020_R01DetailList: (valueIndex) =>
        set((state) => ({
            ...state,
            TQC1020_R01DetailList: state.TQC1020_R01DetailList.filter(
                (item) => item.valueIndex !== valueIndex
            ),
        })),

    // PUR1030의 updatePUR1030_R01DetailList 패턴 그대로
    updateTQC1020_R01DetailList: (valueIndex, data) =>
        set((state) => ({
            ...state,
            TQC1020_R01DetailList: state.TQC1020_R01DetailList.map((item) =>
                item.valueIndex === valueIndex ? data : item
            ),
        })),

    // ── 선택 상태 (PUR1030 패턴) ─────────────────────────────────────────
    selectedTQC1020_R01DetailList: [],
    setSelectedTQC1020_R01DetailList: (idList) =>
        set((state) => ({
            ...state,
            selectedTQC1020_R01DetailList: idList,
        })),
    addSelectedTQC1020_R01DetailList: (idList) =>
        set((state) => {
            const _list = [...state.selectedTQC1020_R01DetailList];
            if (idList.length > 0) _list.push(...idList);
            return { ...state, selectedTQC1020_R01DetailList: _list };
        }),
    removeSelectedTQC1020_R01DetailList: (idList?) =>
        set((state) => ({
            ...state,
            selectedTQC1020_R01DetailList: state.selectedTQC1020_R01DetailList.filter((v) =>
                idList ? !idList.includes(v) : false
            ),
        })),
    initSelectedTQC1020_R01DetailList: () => set({ selectedTQC1020_R01DetailList: [] }),

    // ── 삭제 표시 (PUR1030 패턴) ─────────────────────────────────────────
    deletedTQC1020_R01DetailList: [],
    setDeletedTQC1020_R01DetailList: (idList) =>
        set((state) => ({
            ...state,
            deletedTQC1020_R01DetailList: idList,
        })),
    addDeletedTQC1020_R01DetailList: (idList) =>
        set((state) => {
            const _list = [...state.deletedTQC1020_R01DetailList];
            for (const valueIndex of idList) {
                const isInserted = state.TQC1020_R01DetailList.some(
                    (item) => item.valueIndex === Number(valueIndex) && item.inserted
                );
                const isModified = state.TQC1020_R01DetailList.some(
                    (item) => item.valueIndex === Number(valueIndex) && item.isModified
                );
                if (!isInserted && !isModified) {
                    _list.push(valueIndex);
                }
            }
            return { ...state, deletedTQC1020_R01DetailList: _list };
        }),
    removeDeletedTQC1020_R01DetailList: (idList?) =>
        set((state) => ({
            ...state,
            deletedTQC1020_R01DetailList: state.deletedTQC1020_R01DetailList.filter((v) =>
                idList ? !idList.includes(v) : false
            ),
        })),

    // ── 원본 (PUR1030 패턴) ──────────────────────────────────────────────
    originalTQC1020_R01DetailList: [],
    setOriginalTQC1020_R01DetailList: (data) =>
        set((state) => {
            let newList: TQC1020_R01DetailDto[] = [];
            let valueAndOrderIndex = 1;
            data.forEach((item) => {
                newList.push({
                    ...defaultTQC1020_R01ResultDto,
                    ...item,
                    valueIndex: valueAndOrderIndex,
                    orderIndex: valueAndOrderIndex,
                    orderIndexSeq: valueAndOrderIndex,
                });
                valueAndOrderIndex++;
            });
            return { ...state, originalTQC1020_R01DetailList: newList };
        }),

    // ── 컬럼 상태 ────────────────────────────────────────────────────────
    columnState: null,
    setColumnState: (state) => set({ columnState: state }),
    detailColumnState: null,
    setDetailColumnState: (state) => set({ detailColumnState: state }),

    detailFetchTrigger: false,
    setDetailFetchTrigger: (value) => set({ detailFetchTrigger: value }),

    scrollTop: null,
    setScrollTop: (top) => set({ scrollTop: top }),
    detailScrollTop: null,
    setDetailScrollTop: (top) => set({ detailScrollTop: top }),

    initData: () =>
        set((state) => ({
            ...state,
            TQC1020_R01Filter: defaultTQC1020_R01Filter,
            TQC1020_R01List: [],
            selectedTQC1020_R01: undefined,
            TQC1020_R01DetailList: [],
            originalTQC1020_R01DetailList: [],
            selectedTQC1020_R01DetailList: [],
            deletedTQC1020_R01DetailList: [],
            detailFetchTrigger: false,
            scrollTop: null,
            detailScrollTop: null,
        })),
}));
